import { Table } from '@radix-ui/themes'
import { SortDirection } from '@tanstack/react-table'
import { Input } from 'designSystem/components/Input'
import { useColumnFilterContext } from 'features/knowledgeDocumentViewer/components/table/enhancements/filters/ColumnFilterContext'
import { useClickOutside } from 'features/knowledgeDocumentViewer/components/table/enhancements/filters/filterUtils'
import {
  GECKO_POWER_TABLE_FLAT_DENSITY,
  GECKO_POWER_TABLE_ROW_BG,
  GECKO_POWER_TABLE_ROW_HOVER_BG,
} from 'features/knowledgeDocumentViewer/components/table/TableStyles'
import SortIcon from 'features/knowledgeDocumentViewer/components/table/utility/SortIcon'
import { Filter, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { InputType } from 'types'

interface HeaderCellProps {
  cellText: string
  columnId: string
  onClick?: (event: unknown) => void
  canSort?: boolean
  isSorted?: false | SortDirection
  canFilter?: boolean
  isFiltered?: boolean
}

const StyledTableColumnHeaderCell = styled(Table.ColumnHeaderCell)<{
  $hasOpenDropdown?: boolean
}>`
  min-width: 6.5rem;
  height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
  padding: 0;
  vertical-align: middle;
  box-sizing: border-box;
  border-bottom: 1px solid var(--border) !important;
  ${({ $hasOpenDropdown }) => $hasOpenDropdown && 'z-index: 10 !important;'}
`

const StyledSortIcon = styled(SortIcon)`
  height: 1rem;
  width: 1rem;
  box-sizing: content-box;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  background-color: transparent;
  position: relative;
  color: var(--foreground);
`

const HeaderCellContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
  box-sizing: border-box;
  min-height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
  padding: 0.25rem ${GECKO_POWER_TABLE_FLAT_DENSITY.cellPadding};
`

const HeaderRow = styled.div<{ $hasCellText: boolean }>`
  display: flex;
  gap: ${GECKO_POWER_TABLE_FLAT_DENSITY.cellPadding};
  align-items: center;
  justify-content: space-between;
  min-height: 2rem;
  color: var(--muted-foreground);
  font-size: ${GECKO_POWER_TABLE_FLAT_DENSITY.fontSize};
  font-weight: 500;
  line-height: ${GECKO_POWER_TABLE_FLAT_DENSITY.lineHeight};
  cursor: ${({ $hasCellText }) => ($hasCellText ? 'pointer' : 'default')};
  padding: 0;
  border-radius: var(--radius-sm);
  &:hover {
    background-color: ${({ $hasCellText }) =>
      $hasCellText ? GECKO_POWER_TABLE_ROW_HOVER_BG : null};
    color: ${({ $hasCellText }) => ($hasCellText ? 'var(--foreground)' : null)};
  }
`

const FilterIconButton = styled.button.attrs({
  type: 'button',
})<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex-shrink: 0;
  width: 1rem;
  min-width: 1rem;
  height: 1rem;
  min-height: 1rem;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  color: ${({ $isActive }) =>
    $isActive ? 'var(--foreground)' : 'var(--muted-foreground)'};
  background-color: ${({ $isActive }) =>
    $isActive ? GECKO_POWER_TABLE_ROW_BG : 'transparent'};
  cursor: pointer;

  &:hover {
    color: var(--foreground);
    background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const FilterPillWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.1875rem;
  margin-top: 0.25rem;
  max-height: 3rem;
  overflow-y: auto;
  scrollbar-width: thin;
`

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0.1875rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.6875rem;
  font-weight: 400;
  cursor: pointer;
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  color: ${({ theme }) => theme.forecast.activeText};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
`

const PillText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const PillClose = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  border-radius: 50%;
  padding: 0.1875rem;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: max-content;
  min-width: max(100%, 11rem);
  max-width: 18rem;
  padding: 0.375rem 0.5rem;
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  box-shadow:
    0 0.0625rem 0.125rem 0 ${({ theme }) => theme.forecast.boxShadow},
    0 0.25rem 0.5rem 0 ${({ theme }) => theme.forecast.boxShadow};
`

const MultiSelectCheckbox = styled.input`
  width: 0.875rem;
  min-width: 0.875rem;
  height: 0.875rem;
  min-height: 0.875rem;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.forecast.primary};
`

const MultiSelectOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.defaultText};
  &:hover {
    background: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const MultiSelectList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 12.5rem;
  overflow-y: auto;
  padding: 0.25rem 0;
`

const EmptyMessage = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem;
  opacity: 0.6;
`

const InlineFilterDropdown: React.FC<{
  columnId: string
  columnHeader: string
  onClose: () => void
}> = ({ columnId, columnHeader, onClose }) => {
  const ctx = useColumnFilterContext()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')

  const column = ctx?.getColumn(columnId)
  const columnFilterValue = column?.getFilterValue() as string | undefined

  useEffect(() => {
    if (columnFilterValue) setValue(columnFilterValue)
  }, [columnFilterValue])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleClose = useCallback(() => {
    if (!value && ctx) {
      ctx.setActiveFilterIds((prev) => prev.filter((id) => id !== columnId))
      column?.setFilterValue(undefined)
    }
    onClose()
  }, [value, ctx, columnId, column, onClose])

  useClickOutside(dropdownRef, handleClose)

  if (!ctx || !column) return null

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    column.setFilterValue(newValue || undefined)
    if (!ctx.disableUrlFilters) {
      ctx.navigate({
        to: '.',
        search: (prev) => ({ ...prev, [columnId]: newValue || undefined }),
      })
    }
  }

  return (
    <FilterDropdown ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') handleClose()
        }}
        placeholder={`Filter ${columnHeader.toLowerCase()}...`}
      />
    </FilterDropdown>
  )
}

const InlineMultiSelectDropdown: React.FC<{
  columnId: string
  columnHeader: string
  onClose: () => void
}> = ({ columnId, columnHeader, onClose }) => {
  const ctx = useColumnFilterContext()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')

  const column = ctx?.getColumn(columnId)
  const initialValue = (column?.getFilterValue() as string[] | undefined) ?? []
  const [selected, setSelected] = useState<string[]>(initialValue)
  const selectedRef = useRef(selected)
  selectedRef.current = selected

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  const uniqueValues = useMemo(() => {
    if (!column) return []
    const vals: string[] = []
    for (const key of column.getFacetedUniqueValues().keys()) {
      if (typeof key === 'string' && key.length > 0) vals.push(key)
    }
    return vals.sort()
  }, [column])

  const filtered = useMemo(() => {
    if (!search) return uniqueValues
    const lower = search.toLowerCase()
    return uniqueValues.filter((v) => v.toLowerCase().includes(lower))
  }, [uniqueValues, search])

  const handleClose = useCallback(() => {
    if (selectedRef.current.length === 0 && ctx) {
      ctx.setActiveFilterIds((prev) => prev.filter((id) => id !== columnId))
      column?.setFilterValue(undefined)
    }
    onClose()
  }, [ctx, columnId, column, onClose])

  useClickOutside(dropdownRef, handleClose)

  if (!ctx || !column) return null

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    setSelected(next)
    column.setFilterValue(next.length > 0 ? next : undefined)
  }

  return (
    <FilterDropdown ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      <Input
        ref={searchRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search ${columnHeader.toLowerCase()}...`}
      />
      <MultiSelectList>
        {filtered.map((val) => (
          <MultiSelectOption key={val}>
            <MultiSelectCheckbox
              type='checkbox'
              checked={selected.includes(val)}
              onChange={() => toggle(val)}
            />
            {val}
          </MultiSelectOption>
        ))}
        {filtered.length === 0 && <EmptyMessage>No options</EmptyMessage>}
      </MultiSelectList>
    </FilterDropdown>
  )
}

export const HeaderCell: React.FC<HeaderCellProps> = ({
  cellText,
  columnId,
  canSort,
  onClick,
  isSorted,
  canFilter,
  isFiltered,
}) => {
  const hasCellText = cellText !== undefined && cellText !== ''
  const filterContext = useColumnFilterContext()
  const [showDropdown, setShowDropdown] = useState(false)

  const columnDef = filterContext?.columns.find((col) => col.id === columnId)
  const inputType = columnDef?.meta?.inputType
  const isMultiSelect = inputType === InputType.MultiSelect

  const column = filterContext?.getColumn(columnId)
  const rawFilterValue = column?.getFilterValue()
  const textFilterValue = isMultiSelect
    ? undefined
    : (rawFilterValue as string | undefined)
  const multiFilterValue = isMultiSelect
    ? (rawFilterValue as string[] | undefined)
    : undefined

  const hasFilterValue = isMultiSelect
    ? !!(multiFilterValue && multiFilterValue.length > 0)
    : !!textFilterValue

  const handleFilterClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    filterContext?.addFilter(columnId)
    if (inputType === InputType.Text || isMultiSelect) {
      setShowDropdown((prev) => !prev)
    }
  }

  const handlePillClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDropdown((prev) => !prev)
  }

  const clearFilter = (e: React.MouseEvent) => {
    e.stopPropagation()
    column?.setFilterValue(undefined)
    filterContext?.setActiveFilterIds((prev) =>
      prev.filter((id) => id !== columnId)
    )
    setShowDropdown(false)
    if (filterContext && !filterContext.disableUrlFilters) {
      filterContext.navigate({
        to: '.',
        search: (prev) => ({ ...prev, [columnId]: undefined }),
      })
    }
  }

  const closeDropdown = useCallback(() => setShowDropdown(false), [])

  return (
    <StyledTableColumnHeaderCell key={columnId} $hasOpenDropdown={showDropdown}>
      <HeaderCellContent>
        <HeaderRow $hasCellText={hasCellText} onClick={onClick}>
          <div>{cellText}</div>
          {canSort &&
            isSorted &&
            hasCellText &&
            (isSorted === 'asc' ? (
              <StyledSortIcon sortDirection='asc' />
            ) : (
              <StyledSortIcon sortDirection='desc' />
            ))}
          {canFilter && hasCellText && filterContext && (
            <FilterIconButton
              onClick={handleFilterClick}
              $isActive={!!isFiltered || hasFilterValue}
              aria-label={`Filter ${cellText}`}>
              <Filter size={16} />
            </FilterIconButton>
          )}
        </HeaderRow>

        {textFilterValue && (
          <FilterPillWrap>
            <FilterPill onClick={handlePillClick}>
              <PillText>{textFilterValue}</PillText>
              <PillClose onClick={clearFilter}>
                <X size={10} />
              </PillClose>
            </FilterPill>
          </FilterPillWrap>
        )}

        {isMultiSelect && multiFilterValue && multiFilterValue.length > 0 && (
          <FilterPillWrap>
            <FilterPill onClick={handlePillClick}>
              <PillText>{multiFilterValue.length} selected</PillText>
              <PillClose onClick={clearFilter}>
                <X size={10} />
              </PillClose>
            </FilterPill>
          </FilterPillWrap>
        )}

        {showDropdown &&
          (isMultiSelect ? (
            <InlineMultiSelectDropdown
              columnId={columnId}
              columnHeader={cellText}
              onClose={closeDropdown}
            />
          ) : (
            <InlineFilterDropdown
              columnId={columnId}
              columnHeader={cellText}
              onClose={closeDropdown}
            />
          ))}
      </HeaderCellContent>
    </StyledTableColumnHeaderCell>
  )
}
